import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../shared/colors';

const CommonDeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  question,
  warning,
  itemName,
  confirmText,
  cancelText
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = (...args) => {
    if (isDeleting) return;
    onClose(...args);
  };

  const handleConfirm = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await Promise.resolve(onConfirm());
    } catch (err) {
      console.error("Delete confirmation failed:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={isDeleting}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        className: 'pm-modal-paper'
      }}
    >
      <DialogTitle sx={{ pt: 4, pb: 1, position: 'relative' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: COLORS.ui.error8, color: COLORS.red.muiError, width: 56, height: 56 }}>
            <WarningIcon sx={{ fontSize: '2rem' }} />
          </Avatar>
          <Typography variant="h6" fontWeight="700" color="text.primary" align="center" sx={{ mt: 1 }}>
            {title}
          </Typography>
        </Box>

      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2, textAlign: 'center' }}>
        {question && (
          <Typography sx={{ mb: 1.5, color: 'text.primary', fontSize: '0.95rem' }}>
            {question}
          </Typography>
        )}
        {itemName && (
          <Box sx={{ bgcolor: COLORS.background, border: `1px solid ${COLORS.slate[200]}`, borderRadius: '6px', py: 1.5, px: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="700" color={COLORS.red.muiError} sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
              {itemName}
            </Typography>
          </Box>
        )}
        {warning && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {warning}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 4, pt: 1, display: 'flex', gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isDeleting}
          sx={{ flex: 1, color: 'text.primary', borderColor: COLORS.gray[300], py: '0.5rem', textTransform: 'none', fontWeight: 600 }}
        >
          {cancelText || t('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isDeleting}
          sx={{ flex: 1, bgcolor: COLORS.red.muiError, color: COLORS.white, py: '0.5rem', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: COLORS.red.muiErrorHover } }}
        >
          {isDeleting ? t('common.deleting', 'Deleting...') : (confirmText || t('common.delete', 'Delete'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommonDeleteConfirmDialog;
